import { Chain } from "@rainbow-me/rainbowkit";
import * as Sentry from "@sentry/react";
import { debounce, isEqual } from "lodash-es";
import { createAsyncAction, errorResult, successResult } from "pullstate";
import { Address, formatUnits, Hash, isAddress } from "viem";
import { z } from "zod";
import {
  BRIDGED_USDC_ARB_ADDRESS,
  DUMMY_WALLET,
  NETWORK_TO_TOKEN_ADDRESS_MAP,
  QUOTE_DEBOUNCE_TIME,
  VALID_APPROVAL_SPENDERS,
} from "../../config/consts";
import { responseBuyTokenSchema, responseSellTokenSchema } from "../../generated-schemas";
import {
  commonApiJamApiTypesQuoteResponseSchema,
  errorResponseSchema,
  quoteApiResponseSchema,
  routerApiApiTypesQuoteResponseSchema,
  routeTypeSchema,
} from "../../generated-schemas-router";
import { Amount } from "../../types/amount";
import { JamMessage } from "../../types/message";
import { Quote, QuoteError, QuoteTokenData, QuoteTokens } from "../../types/quote";
import { getWrappedSymbolForChain } from "../../utils/chainUtils";
import { OrderType } from "../../utils/orderTypeUtils";
import { getPermitToUse, PermitType } from "../../utils/permitUtils";
import { getQuoteUrl, isBestQuoteJam, isBestQuotePmm } from "../../utils/quoteUtils";
import { GlobalStore, IGlobalStore, MakerToken, Receiver, TakerToken } from "./GlobalStore";
import { Token } from "./TokenEnum";
import { TokenList } from "./getAllTokenLists";
import { QUOTE_ERROR_CODES_MAPPING } from "../../config/errorCodes";

export const fetchQuote = async ({
  orderType,
  takerTokens,
  makerTokens,
  network,
  address,
  tokenList,
  receiver,
  permitType,
}: GetQuoteParams) => {
  const forceSolver = GlobalStore.getRawState().forceSolver;

  const quoteUrl: string | undefined = getQuoteUrl({
    orderType,
    takerTokens,
    makerTokens,
    network,
    address,
    tokenList,
    receiver,
    permitType,
    forceSolver,
  });
  if (quoteUrl) {
    console.log(`FETCH QUOTE APPROVAL TYPE: `, permitType);
    const response = await fetch(quoteUrl, {
      method: "GET",
    });
    if (response.ok) {
      return response.json();
    } else {
      Sentry.captureException(`Quote Response endpoint returned a bad status.`, {
        extra: { error: response },
      });
      GlobalStore.update(s => {
        s.quoteError = {
          error: {
            errorCode: response.status,
            message: "HTTP Error code",
          },
        };
      });
    }
  }
  return Promise.resolve(undefined);
};

export interface GetQuoteParams {
  orderType: OrderType;
  takerTokens: Array<TakerToken>;
  makerTokens: Array<MakerToken>;
  network: Chain;
  address: Address;
  tokenList: TokenList | undefined;
  receiver: Receiver;
  permitType: PermitType | undefined;
  forceRouteType?: RouteType | undefined;
}

export type RouteType = z.infer<typeof routeTypeSchema>;
type PmmQuoteResponse = z.infer<typeof quoteApiResponseSchema>;
type JamQuoteResponse = z.infer<typeof commonApiJamApiTypesQuoteResponseSchema>;

type BuyTokenResponse = z.infer<typeof responseBuyTokenSchema>;
type TokenResponse = BuyTokenResponse | z.infer<typeof responseSellTokenSchema>;

export const transformQuoteToken = (key: string, value: TokenResponse): QuoteTokenData => {
  const amount = new Amount(formatUnits(BigInt(value?.amount ?? 0), value?.decimals));
  const amountUsd = amount.mul(new Amount(value.priceUsd.toString()));
  return {
    amount: amount,
    amountUsd: amountUsd,
    contractAddress: key as Address,
    priceUsd: new Amount(String(value?.priceUsd)),
    decimals: value?.decimals,
    ...(value?.price !== undefined && { price: new Amount(String(value?.price)) }),
    ...(value?.priceBeforeFee !== undefined && {
      priceBeforeFee: new Amount(String(value?.priceBeforeFee)),
    }),
  };
};

const transformQuoteTokenBuy = (key: string, value: BuyTokenResponse): QuoteTokenData => {
  const token = transformQuoteToken(key, value);
  return {
    ...token,
    amountBeforeFee: new Amount(formatUnits(BigInt(value?.amountBeforeFee ?? 0), value?.decimals)),
  };
};

type QuoteApiResponse = PmmQuoteResponse | JamQuoteResponse;

const transformQuoteBuyTokens = (buyTokens: QuoteApiResponse["buyTokens"]): QuoteTokens => {
  return Object.fromEntries(
    Object.entries(buyTokens).map(([key, value]) => {
      try {
        // TODO: Remove hardcoded condition for Bridged USDC when we move to indexing by contract address
        const tokenSymbol = key === BRIDGED_USDC_ARB_ADDRESS ? "USDC.e" : value.symbol;
        const transformedValue = transformQuoteTokenBuy(key, value);
        return [tokenSymbol, transformedValue];
      } catch (e) {
        console.error(`Could not transform quote token: `, e);
        return [key, value];
      }
    })
  ) as QuoteTokens;
};

const transformQuoteSellTokens = (buyTokens: QuoteApiResponse["sellTokens"]): QuoteTokens => {
  return Object.fromEntries(
    Object.entries(buyTokens).map(([key, value]) => {
      try {
        const tokenSymbol = key === BRIDGED_USDC_ARB_ADDRESS ? "USDC.e" : value.symbol;
        const transformedValue = transformQuoteToken(key, value);
        return [tokenSymbol, transformedValue];
      } catch (e) {
        console.error(`Could not transform quote token: `, e);
        return [key, value];
      }
    })
  ) as QuoteTokens;
};

const transformJamQuoteResponse = (quoteResponse: JamQuoteResponse): Quote => {
  const expiry = new Amount(quoteResponse.expiry.toString());
  const gasFee = new Amount((quoteResponse.gasFee.usd ?? 0).toString());
  const quote: Quote = {
    expiry: expiry,
    routeType: "Jam" as RouteType,
    type: quoteResponse.type,
    quoteId: quoteResponse.quoteId,
    chainId: quoteResponse.chainId,
    receiver: quoteResponse.receiver as `0x${string}`,
    makerTokens: transformQuoteBuyTokens(quoteResponse.buyTokens),
    takerTokens: transformQuoteSellTokens(quoteResponse.sellTokens),
    usdGasFee: gasFee,
    requiredSignatures: quoteResponse.requiredSignatures as Array<`0x${string}`>,
    approvalTarget: quoteResponse.approvalTarget as Hash,
    settlementAddress: quoteResponse.settlementAddress as Hash,
    solver: quoteResponse.solver,
    toSign: {
      expiry: BigInt(quoteResponse.toSign.expiry),
      taker: quoteResponse.toSign.taker as Hash,
      receiver: quoteResponse.toSign.receiver as Hash,
      executor: quoteResponse.toSign.executor,
      minFillPercent: quoteResponse.toSign.minFillPercent,
      nonce: BigInt(quoteResponse.toSign.nonce),
      hooksHash: quoteResponse.toSign.hooksHash as Hash,
      sellTokens: quoteResponse.toSign.sellTokens as Array<Hash>,
      buyTokens: quoteResponse.toSign.buyTokens as Array<Hash>,
      sellAmounts: quoteResponse.toSign.sellAmounts.map(entry => BigInt(entry)),
      buyAmounts: quoteResponse.toSign.buyAmounts.map(entry => BigInt(entry)),
      sellNFTIds: quoteResponse.toSign.sellNFTIds.map(entry => BigInt(entry)),
      buyNFTIds: quoteResponse.toSign.buyNFTIds.map(entry => BigInt(entry)),
      sellTokenTransfers: quoteResponse.toSign.sellTokenTransfers,
      buyTokenTransfers: quoteResponse.toSign.buyTokenTransfers,
    } as JamMessage,
  };
  return quote;
};
const transformPmmQuoteResponse = (quoteResponse: PmmQuoteResponse): Quote => {
  const expiry = new Amount(quoteResponse.expiry.toString());
  const gasFee = new Amount((quoteResponse.gasFee.usd ?? 0).toString());
  const quote: Quote = {
    type: quoteResponse.type,
    routeType: "PMM" as RouteType,
    quoteId: quoteResponse.quoteId,
    chainId: quoteResponse.chainId,
    receiver: quoteResponse.receiver as `0x${string}`,
    expiry: expiry,
    makerTokens: transformQuoteBuyTokens(quoteResponse.buyTokens),
    takerTokens: transformQuoteSellTokens(quoteResponse.sellTokens),
    usdGasFee: gasFee,
    requiredSignatures: quoteResponse.requiredSignatures as Array<`0x${string}`>,
    approvalTarget: quoteResponse.approvalTarget as Hash,
    settlementAddress: quoteResponse.settlementAddress as Hash,
    toSign: {
      expiry: BigInt(quoteResponse.toSign.expiry),
      taker_address: quoteResponse.toSign.taker_address as Hash,
      maker_addresses: quoteResponse.toSign.maker_addresses as Array<Hash>,
      maker_nonces: quoteResponse.toSign.maker_nonces.map(entry => BigInt(entry)),
      taker_tokens: quoteResponse.toSign.taker_tokens as Array<Array<Hash>>,
      maker_tokens: quoteResponse.toSign.maker_tokens as Array<Array<Hash>>,
      taker_amounts: quoteResponse.toSign.taker_amounts.map(entry =>
        entry.map(nestedEntry => BigInt(nestedEntry))
      ),
      maker_amounts: quoteResponse.toSign.maker_amounts.map(entry =>
        entry.map(nestedEntry => BigInt(nestedEntry))
      ),
      receiver: quoteResponse.toSign.receiver as Hash,
      commands: quoteResponse.toSign.commands as Hash,
    },
  };
  return quote;
};

const getQuoteSafe = async (params: GetQuoteParams) => {
  const routerResponse = await fetchQuote(params);
  const errorResult = errorResponseSchema.safeParse(routerResponse);
  if (errorResult.success) {
    return { error: errorResult.data.error } as QuoteError;
  }
  const validationResult = routerApiApiTypesQuoteResponseSchema.safeParse(routerResponse);
  if (validationResult.success) {
    console.log(`Route type:`, params.forceRouteType ?? "BEST");
    const bestRoute = params.forceRouteType
      ? validationResult.data.routes.find(r => r.type === params.forceRouteType)
      : validationResult.data.routes[0];
    if (!bestRoute) {
      Sentry.captureMessage("There was no route & wasn't a JAM and PMM error on response");
      return {
        error: {
          errorCode: 101,
          message: QUOTE_ERROR_CODES_MAPPING[101],
        },
      };
    }

    if (VALID_APPROVAL_SPENDERS.includes(bestRoute.quote.approvalTarget)) {
      if (isBestQuotePmm(bestRoute.type)) {
        return transformPmmQuoteResponse(bestRoute.quote as PmmQuoteResponse);
      } else if (isBestQuoteJam(bestRoute.type)) {
        return transformJamQuoteResponse(bestRoute.quote as JamQuoteResponse);
      }
    } else {
      const message = `Invalid approval target`;
      console.error(message);
      Sentry.captureException(message);
    }
  } else {
    const error = validationResult.error.format();
    const message = `Quote endpoint returned a bad response.`;
    console.error(message, error);
    Sentry.captureException(message, {
      extra: { error: error },
    });
  }
};
export const getQuote = createAsyncAction(
  async (params: GetQuoteParams) => {
    try {
      GlobalStore.update(s => {
        s.isRequestingQuote = true;
      });
      if (!params.permitType) {
        Sentry.captureException(
          `PermitType was undefined, potential race condition, trying obtain permit type again.`
        );
      }
      const permitType = params.permitType
        ? params.permitType
        : await getPermitToUse(
            params.orderType,
            GlobalStore.getRawState().address as `0x${string}`,
            params.takerTokens,
            params.tokenList ?? {},
            params.network
          );

      const result = await getQuoteSafe({
        orderType: params.orderType,
        takerTokens: params.takerTokens,
        makerTokens: params.makerTokens,
        network: params.network,
        address: params.address,
        tokenList: params.tokenList,
        receiver: params.receiver,
        permitType: permitType,
        forceRouteType: params.forceRouteType,
      });
      if (result && isQuoteError(result)) {
        console.error(`Quote Server Responded with an error: `, result.error);
        GlobalStore.update(s => {
          s.quoteError = result;
          s.isRequestingQuote = false;
          s.quote = undefined;
        });
      } else {
        // Clean up any previous errors
        GlobalStore.update(s => {
          s.quoteError = undefined;
        });
      }

      if (result) {
        return successResult(result);
      }
    } catch (e) {
      console.log(`Could not get quote for network ${params.network.name}`, e);
      return errorResult([], `Could not get quote for network ${params.network.name}`, e);
    }
    return errorResult([], `Couldn't get quote for network: ${params.network.name}`);
  },
  {
    postActionHook: ({ result }) => {
      GlobalStore.update(s => {
        s.isRequestingQuote = false;
      });
      const quote = result.payload;
      if (!result.error && quote && isQuote(quote)) {
        GlobalStore.update(s => {
          if (isQuoteStale(quote as unknown as Quote, s)) {
            const message = "Quote is stale, not updating store.";
            console.log(message);
            Sentry.captureMessage(message);
          } else {
            s.quote = quote as unknown as Quote;
          }
        });
      }
    },
  }
);

const isQuoteStale = (quote: Quote, uiState: IGlobalStore): boolean => {
  const quoteTakerTokens = Object.values(quote.takerTokens).map(token => token.contractAddress);
  const wrappedTokenAddress =
    NETWORK_TO_TOKEN_ADDRESS_MAP[uiState.network.id][getWrappedSymbolForChain(uiState.network)];
  const nativeTokenAddress =
    NETWORK_TO_TOKEN_ADDRESS_MAP[uiState.network.id][
      uiState.network.nativeCurrency.symbol as Token
    ];

  const takerTokensReplaceNative = quoteTakerTokens.map(address =>
    address === wrappedTokenAddress ? nativeTokenAddress : address
  );

  const tokenList = uiState?.tokenLists?.[uiState.network.id];
  if (!tokenList) {
    return true;
  }

  const makerTokens = Object.values(quote.makerTokens).map(token => token.contractAddress);
  const storeTakerTokens = uiState.takerTokens.map(
    e => tokenList[e.token]?.chainInfo.contractAddress
  );
  const storeMakerTokens = uiState.makerTokens.map(
    e => tokenList[e.token]?.chainInfo.contractAddress
  );
  const storeAmounts = uiState.takerTokens.map(e => e.amount);
  const quoteAmounts = Object.values(quote.takerTokens).map(e => e.amount);
  const amountsMatch = quoteAmounts.every((amount, i) => amount?.value.eq(storeAmounts[i].value));
  const userAddress = uiState.address ? uiState.address : DUMMY_WALLET;
  const quoteReceiverAddressParam = isAddress(uiState.receiver.address)
    ? uiState.receiver.address
    : userAddress;
  const receiverMatch = quoteReceiverAddressParam === quote.receiver;
  const makerTokensMatch = isEqual(makerTokens, storeMakerTokens);
  const isTakerTokenNativeToken = storeTakerTokens.includes(nativeTokenAddress);
  const takerTokensMatch = isTakerTokenNativeToken
    ? isEqual(takerTokensReplaceNative, storeTakerTokens)
    : isEqual(quoteTakerTokens, storeTakerTokens);
  const isFresh = takerTokensMatch && makerTokensMatch && amountsMatch && receiverMatch;
  return !isFresh;
};

export const getQuoteDebounced = debounce(
  (params: GetQuoteParams) => {
    void getQuote.run(params);
    GlobalStore.update(s => {
      s.isFirstQuoteForTrade = false;
    });
  },
  QUOTE_DEBOUNCE_TIME,
  {
    leading: false,
    trailing: true,
  }
);

const isQuoteError = (maybeQuoteResponse: Quote | QuoteError): maybeQuoteResponse is QuoteError => {
  return "error" in maybeQuoteResponse;
};

const isQuote = (maybeQuoteResponse: Quote | QuoteError): maybeQuoteResponse is Quote => {
  return !isQuoteError(maybeQuoteResponse);
};
